import { useEffect, useRef, useState } from 'react'
import { listCustomers } from '@/lib/api'

// Search-as-you-type over the customer list, shared by the composer's customer
// pin (CustomerPicker) and its @-mentions. Deliberately the SAME search the
// Customers screen runs — `listCustomers({ search })`, one ilike pass over name,
// email and phone — so a name that finds someone on that screen finds them here.
//
// 4.5k customers, so this is always debounced and always limited; the sequence
// guard drops a slow response that lands after a newer keystroke.

export interface CustomerMatch {
  id: string
  name: string | null
  phone: string | null
}

/** Below this the result set is everybody, which is not a search. */
export const MIN_QUERY_LENGTH = 2

interface Options {
  /** Skip searching entirely (menu closed, wrong role). */
  enabled?: boolean
  limit?: number
  debounceMs?: number
}

export interface CustomerSearchState {
  matches: CustomerMatch[]
  searching: boolean
  /** True once the query is long enough to have been searched. */
  active: boolean
}

export function useCustomerSearch(query: string, opts: Options = {}): CustomerSearchState {
  const { enabled = true, limit = 8, debounceMs = 250 } = opts
  const [matches, setMatches] = useState<CustomerMatch[]>([])
  const [searching, setSearching] = useState(false)
  const seqRef = useRef(0)

  const trimmed = query.trim()
  const active = trimmed.length >= MIN_QUERY_LENGTH

  useEffect(() => {
    if (!enabled || !active) {
      // Bump the sequence so an in-flight response cannot repopulate a closed menu.
      seqRef.current += 1
      setMatches([])
      setSearching(false)
      return
    }

    setSearching(true)
    const seq = ++seqRef.current
    const timer = setTimeout(() => {
      listCustomers({ search: trimmed, limit })
        .then((page) => {
          if (seq !== seqRef.current) return
          setMatches(page.items.map((c) => ({ id: c.id, name: c.name ?? null, phone: c.phone ?? null })))
        })
        .catch(() => {
          if (seq === seqRef.current) setMatches([])
        })
        .finally(() => {
          if (seq === seqRef.current) setSearching(false)
        })
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [enabled, active, trimmed, limit, debounceMs])

  return { matches, searching, active }
}
